<template lang="pug">
  
div( class="form-group row" v-bind:class="{ 'd-none': (type === 'hidden') }" )
  label( class="col-sm-2 col-form-label" ) {{ label }}
    span( v-if="required" ) &nbsp;*
    template( v-if="description" )
      br
      small {{ description }}

    slot( name="description" )
  
  div( class="col-sm-10 d-flex align-items-center" )
    div( class="w-100" )
      input(
        v-if="type === 'hidden'"
        v-bind:name="name"
        v-bind:value="localValue"
        v-bind:required="required"
        v-on:change="onChange"
        data-moonpull-input="true"
      )

      div( v-else-if="type === 'select'" class="w-100" )

        div( v-if="filterable" class="position-relative filterable-container" )

          input(
            type="hidden"
            v-bind:name="name"
            v-bind:value="localValue"
            v-bind:required="required"
            v-on:change="onChange"
            data-moonpull-input="true"
          )
          
          div( class="input-group" )
            input(
              class="form-control"
              v-bind:class="{ 'selected': !! localValue }"
              type="text"
              placeholder="Search..."
              v-bind:disabled="disabled || !! localValue"
              v-model="searchString"
              v-on:focus="onSearchFocus"
            )
            div( class="input-group-append" v-if="localValue && ! disabled" )
              button( class="btn btn-outline-secondary" v-on:click="onSelect({ label: '', value: null })" )
                i( class="fa fa-times" )
          
          ul( role="listbox" class="typehead dropdown-menu" v-bind:class="{ 'show': showSearchOptions }" )
            li( class="close" v-on:click="showSearchOptions = false" )
              i( class="fa fa-times" )

            li( v-for="o in filteredOptions" v-on:click="onSelect(o)" )
              a( class="dropdown-item" )
                strong {{ o.label }}
                span( v-if="o.description" )
                  br
                  small {{ o.description }}

        div( v-else )
          select(
            class="form-control"
            v-bind:name="name"
            v-bind:value="localValue"
            v-bind:required="required"
            v-bind:disabled="disabled"
            v-bind:multiple="multiple"
            v-on:change="onChange"
            data-moonpull-input="true"
          )
            option( v-for="o in options" v-bind:value="o.value" v-bind:disabled="o.disabled" ) {{ o.label }}
      
      template( v-else-if="type === 'radio'" )
        label( class="block" v-for="o in options" )
          input(
            type="radio"
            v-bind:name="name"
            v-bind:value="o.value"
            v-bind:disabled="disabled"
            v-on:change="onChange"
            data-moonpull-input="true"
          )
          span &nbsp; {{ o.label }}
      
      textarea(
        v-else-if="type === 'textarea'"
        class="form-control"
        v-bind:name="name"
        v-bind:value="localValue"
        v-bind:rows="rows"
        v-bind:required="required"
        v-bind:disabled="disabled"
        v-on:input="onChange"
        data-moonpull-input="true"
        v-bind:data-moonpull-update-null="updateNull"
      )
      
      input(
        v-else-if="type === 'checkbox'"
        type="checkbox"
        v-bind:name="name"
        v-bind:value="localValue"
        v-bind:checked="checked"
        v-bind:required="required"
        v-bind:disabled="disabled"
        v-on:change="onChange"
        data-moonpull-input="true"
      )

      input(
        v-else-if="type === 'date'"
        class="form-control"
        v-bind:type="type"
        v-bind:name="name"
        v-bind:value="(localValue) ? dateFormat(new Date(localValue), 'yyyy-mm-dd') : ''"
        v-bind:required="required"
        v-bind:disabled="disabled"
        v-on:change="onChange"
        data-moonpull-input="true"
        v-bind:data-moonpull-update-null="updateNull"
      )

      input(
        v-else-if="type === 'number'"
        class="form-control"
        v-bind:type="type"
        v-bind:name="name"
        v-bind:value="localValue"
        v-bind:required="required"
        v-bind:disabled="disabled"
        v-bind:min="min"
        v-bind:max="max"
        v-on:change="onChange"
        data-moonpull-input="true"
        v-bind:data-moonpull-update-null="updateNull"
      )
      
      input(
        v-else-if="type === 'file'"
        type="file"
        class="form-control-file"
        v-bind:name="name"
        v-bind:required="required"
        v-bind:disabled="disabled"
        v-bind:multiple="multiple"
        data-moonpull-input="true"
        v-bind:data-moonpull-update-null="updateNull"
        ref="fileInput"
        v-on:change="onFileInputChange"
      )

      input(
        v-else
        class="form-control"
        v-bind:type="type"
        v-bind:name="name"
        v-bind:value="localValue"
        v-bind:required="required"
        v-bind:disabled="disabled"
        v-on:change="onChange"
        data-moonpull-input="true"
        v-bind:data-moonpull-update-null="updateNull"
      )

      div( class="mt-1" )
        slot( name="input-desc" )

</template>

<style lang="scss" scoped>
input[type="checkbox"] {
  transform: scale(1.75); 
}
ul.typehead.dropdown-menu {

  position: relative;
  max-height: 300px;
  overflow-y: scroll;

  .close {
    position: absolute;
    top: 0;
    right: 0;
  }
}
.filterable-container {
  .selected.form-control:disabled {
    background-color: #fff;
  }
}
</style>

<script>
import Vue from 'vue'
import * as dateFormat from 'dateformat'
export default Vue.extend({

  props: {
    name: String,
    type: String,
    label: String,
    description: String,
    value: {
      type: [String, Number, Boolean],
      default: ''
    },
    checked: Boolean,
    options: Array,
    rows: String,
    required: Boolean,
    disabled: Boolean,
    updateNull: Boolean,
    filterable: Boolean,
    multiple: Boolean,
    min: String,
    max: String,
  },

  data() {
    return {
      searchString: '',
      localValue: this.value,
      showSearchOptions: false,
    }
  },

  watch: {
    value(newValue) {
      this.localValue = newValue

      // For filterable selects, null the search value if the newValue is null
      if (newValue === null) {
        this.searchString = ''
      }
    },
  },

  computed: {
    filteredOptions() {
      if (this.searchString) {
        return this.options.filter(option => option.label.toLowerCase().includes(this.searchString.toLowerCase()))
      } else {
        return this.options
      }
    }
  },

  methods: {
    dateFormat,

    onSearchFocus() {
      this.showSearchOptions = true
    },

    onSelect({ label, value, disabled }) {
      if (disabled) {
        return
      }

      this.localValue = value
      this.searchString = (value === null) ? '' : label
      this.showSearchOptions = false
      
      // Spoof event for hidden input
      this.$emit('change', {
        target: {
          value: value,
        },
      })
    },

    onChange(e) {
      const target = e.target;

      switch (target.type) {
        case 'checkbox':
        case 'radio':
          this.localValue = target.value || target.checked;
          break;
        case 'number':
          this.localValue = target.valueAsNumber;
          break;
        case 'date':
          this.localValue = target.valueAsDate;
          break;
        case 'select-multiple':
          this.localValue = Array.from(target.selectedOptions).map(o => o.value)
        default:
          this.localValue = target.value
          break;
      }

      this.$emit('change', e);
    },

    onFileInputChange(e) {
      const fileInput = this.$refs.fileInput
      this.localValue = fileInput.files
      this.$emit('change', e)
    },

  },

  beforeMount() {
    if (this.type === 'select' && this.filterable && this.value) {
      const option = this.options?.find(o => o.value === this.value)
      if (option) {
        this.onSelect({
          label: option.label,
          value: option.value,
        })
      }
    }
  },
})
</script>